import React, {useState} from "react";
import {useCollapsibleRows} from "@vigo/hooks";
import {Card, Table, Button, Space, Tooltip, Row, Popconfirm, message} from "antd";
import {SearchOutlined, FileAddOutlined} from "@ant-design/icons";
import {useStore} from "../../../../hooks/useStore";
import {useFetch} from "../../../../hooks/useFetch";
import {Link} from "react-router-dom";
import {BackofficeCollectionsTabFilters} from "./BackofficeCollectionsTabFilters";
import moment from "moment";
import config from "../../../../_core/config";
import {codPaymentStatuses, codPaymentTypes} from "../../../../constants/constants";
import {isConstantExist} from "../../../../utils/helpers";
import {useTranslation} from "react-i18next";
import {ExpandableStyledTable} from "components/_defaultComponents/ExpandableStyledTable/ExpandableStyledTable.styles";

const PAGE_SIZE = 10;
const formatDate = date => {
    return moment(date).format("DD.MM.YYYY HH:mm");
};

const expandedRowRender = (record, t) => {
    const columns = [
        {
            title: "Payment Id",
            dataIndex: "transactionId",
            key: "transactionId",
            width: 100,
            ellipsis: {
                showTitle: false
            },
            render: text => <Tooltip title={text}>{text}</Tooltip>,
            onCell: () => ({
                "data-label": "Payment Id"
            })
        },
        {
            title: "Ödeme Tutarı",
            dataIndex: "transactionAmount",
            key: "amount",
            width: 100,
            render: text => text && `${text} TL`,
            onCell: () => ({
                "data-label": "Ödeme Tutarı"
            })
        },
        {
            title: "Ödeme yöntemi",
            dataIndex: "paymentType",
            key: "paymentMethod",
            width: 120,
            render: paymentType =>
                `${
                    isConstantExist(codPaymentTypes, paymentType)
                        ? t(`paymentTypes.${paymentType}`, {
                              ns: "codPayment"
                          })
                        : paymentType
                }`,
            onCell: () => ({
                "data-label": "Ödeme Yöntemi"
            })
        },
        {
            title: "İşlem Durumu",
            dataIndex: "providerPaymentStatus",
            key: "providerPaymentStatus",
            width: 120,
            render: text => text?.title,
            onCell: () => ({
                "data-label": "İşlem Durumu"
            })
        },
        {
            title: "İşlem Tarihi",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 150,
            render: text => new Date(text).toLocaleString("tr-TR"),
            onCell: () => ({
                "data-label": "İşlem Tarihi"
            })
        }
    ];

    return (
        <Table
            columns={columns}
            dataSource={record.transactions || []}
            pagination={false}
            rowKey="_id"
            size="small"
        />
    );
};

export const BackofficeCollectionsTab = ({isTabOpened = false}) => {
    const stores = useStore();
    const {t} = useTranslation();
    const [filters, setFilters] = useState({status: "all"});
    const [page, setPage] = useState(1);
    const expandableState = useCollapsibleRows({isTabOpened, page});
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [recall, setRecall] = useState(false);
    const [checkingPaymentId, setCheckingPaymentId] = useState();

    const [loading, payments] = useFetch({
        fetchFn: stores.codPaymentStore.getCodPayments.bind(null, {
            ...filters,
            skip: (page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE
        }),
        dependencies: [filters, page, isTabOpened, recall],
        initialCall: isTabOpened,
        condition: !stores.meStore.checkRole("isCompanyUser") && isTabOpened
    });

    const handleFiltersChange = newFilters => {
        setFilters(newFilters);
        setPage(1);
        setSelectedRowKeys([]);
    };

    // success true dönse bile hata mesajı gelebildiği için tipe bakmadan ikonsuz gösteriliyor
    const showResponseMessage = result => {
        result?.message && message.open({content: result.message});
    };

    const handleCheckPaymentStatusOnPos = async record => {
        setCheckingPaymentId(record._id);
        try {
            const result = await stores.codPaymentStore.checkPaymentStatusOnPos({
                codPaymentId: record._id
            });
            showResponseMessage(result);
            setRecall(!recall);
        } finally {
            setCheckingPaymentId(undefined);
        }
    };

    const handleAddToReference = async codPaymentIds => {
        const result = await stores.codPaymentStore.addPaymentToReference({codPaymentIds});
        showResponseMessage(result);
        if (result?.success) {
            setSelectedRowKeys([]);
        }
        setRecall(!recall);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: selectedKeys => setSelectedRowKeys(selectedKeys),
        // Referans no'ya yalnızca ödemesi alınmış kayıtlar eklenebilir
        getCheckboxProps: record => ({disabled: record.status !== "SUCCESS"})
    };

    const renderActions = record => {
        if (record?.status === "PENDING") {
            return (
                <Tooltip title="İşlem Onayı Sorgula">
                    <Button
                        loading={checkingPaymentId === record._id}
                        size="small"
                        icon={<SearchOutlined />}
                        onClick={() => handleCheckPaymentStatusOnPos(record)}
                    />
                </Tooltip>
            );
        }
        if (record?.status === "SUCCESS" && config?.isDev) {
            return (
                <Popconfirm
                    title="Referans numarasına eklemek istediğinize emin misiniz?"
                    onConfirm={() => handleAddToReference([record._id])}
                    okText="Evet"
                    cancelText="Hayır"
                >
                    <Tooltip title="Referans No'ya Ekle">
                        <Button size="small" icon={<FileAddOutlined />} />
                    </Tooltip>
                </Popconfirm>
            );
        }
        return null;
    };

    const columns = [
        {
            title: "Firma",
            dataIndex: ["deliveryInfo", "order", "pickupInfoCompanyName"],
            key: "company",
            render: (text, row) => (
                <Link
                    data-testid="companyName"
                    target="_blank"
                    to={{
                        pathname: "/companies/" + row?.deliveryInfo?.order?.pickupInfoCompanyId
                    }}
                >
                    {text}
                </Link>
            ),
            onCell: () => ({
                "data-label": "Firma"
            })
        },
        {
            title: "Alt Firma",
            dataIndex: ["deliveryInfo", "order", "pickupInfoName"],
            key: "subCompany",
            render: (text, row) => (
                <Link
                    data-testid="subCompanyName"
                    target="_blank"
                    to={{
                        pathname: "/companies/sub-company/" + row?.deliveryInfo?.order?.pickupInfoId
                    }}
                >
                    {text}
                </Link>
            ),
            onCell: () => ({
                "data-label": "Alt Firma"
            })
        },
        {
            title: "Ödeme Id",
            dataIndex: ["_id"],
            key: "_id",
            ellipsis: {
                showTitle: false
            },
            render: text => <Tooltip title={text}>{text}</Tooltip>,
            onCell: () => ({
                "data-label": "Ödeme Id"
            })
        },
        {
            title: "Tutar",
            dataIndex: ["payment", "receivedAmount"],
            key: "amount",
            render: text => text && `${text} TL`,
            onCell: () => ({
                "data-label": "Tutar"
            })
        },
        {
            title: "İşlem Tarihi",
            dataIndex: "createdAt",
            key: "createdAt",
            render: text => text && formatDate(text),
            onCell: () => ({
                "data-label": "İşlem Tarihi"
            })
        },
        {
            title: "Ödeme Yöntemi",
            dataIndex: ["payment", "paymentType"],
            key: "paymentMethod",
            render: paymentType =>
                `${
                    isConstantExist(codPaymentTypes, paymentType)
                        ? t(`paymentTypes.${paymentType}`, {
                              ns: "codPayment"
                          })
                        : paymentType
                }`,
            onCell: () => ({
                "data-label": "Ödeme Yöntemi"
            })
        },
        {
            title: "İşlem Durumu",
            dataIndex: ["status"],
            key: "status",
            render: text => {
                const status = codPaymentStatuses?.find(status => status?.value === text);
                return status?.name || text;
            },
            onCell: () => ({
                "data-label": "İşlem Durumu"
            })
        },
        {
            title: "Sipariş No",
            dataIndex: ["delivery", "vigoOrderNumber"],
            key: "vigoOrderNumber",
            render: (text, row) => (
                <Link
                    data-testid="vigoOrderNumber"
                    target="_blank"
                    to={{
                        pathname: "/orders/" + (row?.deliveryInfo?._id || row?.delivery?.deliveryId)
                    }}
                >
                    {text}
                </Link>
            ),
            onCell: () => ({
                "data-label": "Sipariş No"
            })
        },
        {
            title: "Kurye",
            dataIndex: ["deliveryInfo", "assignedCourier", "fullname"],
            key: "courier",
            render: (text, row) => (
                <Link
                    data-testid="courierName"
                    target="_blank"
                    to={{
                        pathname: "/couriers/" + row?.deliveryInfo?.assignedCourier?._id
                    }}
                >
                    <>{text}</>
                </Link>
            ),
            onCell: () => ({
                "data-label": "Kurye"
            })
        },
        {
            title: "Aksiyonlar",
            key: "actions",
            render: (_, record) => <Space>{renderActions(record)}</Space>,
            onCell: () => ({
                "data-label": "Aksiyonlar"
            })
        }
    ];

    return (
        <Card>
            <Row gutter={[16, 16]} style={{marginBottom: 16}}>
                <BackofficeCollectionsTabFilters
                    filters={filters}
                    setFilters={handleFiltersChange}
                />
            </Row>
            {config?.isDev && (
                <Row justify="end" style={{marginBottom: 16}}>
                    <Popconfirm
                        title={`${selectedRowKeys.length} adet ödemeyi referans numarasına eklemek istediğinize emin misiniz?`}
                        onConfirm={() => handleAddToReference(selectedRowKeys)}
                        okText="Evet"
                        cancelText="Hayır"
                        disabled={selectedRowKeys.length === 0}
                    >
                        <Button
                            type="primary"
                            icon={<FileAddOutlined />}
                            disabled={selectedRowKeys.length === 0}
                        >
                            Seçilenleri Referans No'ya Ekle ({selectedRowKeys.length})
                        </Button>
                    </Popconfirm>
                </Row>
            )}
            <ExpandableStyledTable
                expandable={{
                    ...expandableState,
                    expandedRowRender: record => expandedRowRender(record, t),
                    rowExpandable: record => record?.transactions?.length > 0
                }}
                rowSelection={config?.isDev ? rowSelection : undefined}
                loading={loading}
                columns={columns}
                size="small"
                dataSource={payments?.rows || []}
                rowKey="_id"
                pagination={{
                    total: payments?.dataCount || 0,
                    current: page,
                    hideOnSinglePage: true,
                    showSizeChanger: false,
                    pageSize: PAGE_SIZE,
                    onChange: page => {
                        setPage(page);
                        setSelectedRowKeys([]);
                    }
                }}
            />
        </Card>
    );
};
